import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../gql/api';
import _ from 'lodash';

export const getServiceOrdersDataByWorkspaceId = createAsyncThunk(
  'serviceOrders/getServiceOrdersDataByWorkspaceId',
  async (workspaceId) => {
    const serviceOrders = await api.serviceOrders.getServiceOrdersByWorkspaceId(
      {
        workspaceId,
      }
    );

    const columns =
      serviceOrders?.data.getServiceOrderStatusesByWorkspaceId ?? [];

    // Access nested serviceOrders from the new ServiceOrderResult type
    const tasks =
      serviceOrders?.data.getServiceOrdersByWorkspaceId?.serviceOrders ?? [];

    return {
      columns: columns.map(({ description, serviceOrderIds, ...column }) => ({
        ...column,
        taskIds: serviceOrderIds,
        name: description,
      })),
      tasks: tasks.map(({ title, isFinished, serviceOrderLogs, ...task }) => ({
        ...task,
        name: title,
        comments: serviceOrderLogs,
        completed: isFinished,
      })),
    };
  }
);

export const createServiceOrderStatus = createAsyncThunk(
  'serviceOrders/createServiceOrderStatus',
  async (payload) => {
    const { workspaceId, name } = payload;

    const result = await api.serviceOrders.createServiceOrderStatus({
      data: {
        workspaceId,
        description: name,
      },
    });

    const newServiceOrderStatus = result?.data.createServiceOrderStatus;

    return {
      ...newServiceOrderStatus,
      taskIds: newServiceOrderStatus?.serviceOrderIds ?? [],
      name: newServiceOrderStatus?.description,
    };
  }
);

export const updateServiceOrderStatus = createAsyncThunk(
  'serviceOrders/updateServiceOrderStatus',
  async (payload) => {
    const { id, ...data } = payload;

    const result = await api.serviceOrders.updateServiceOrderStatus({
      serviceOrderStatusId: id,
      data: {
        description: data.name,
        order: data.order,
      },
    });

    const newServiceOrderStatus = result?.data.updateServiceOrderStatus;

    return {
      ...newServiceOrderStatus,
      taskIds: newServiceOrderStatus?.serviceOrderIds ?? [],
      name: newServiceOrderStatus?.description,
    };
  }
);

export const deleteServiceOrderStatus = createAsyncThunk(
  'serviceOrders/deleteServiceOrderStatus',
  async (serviceOrderStatusId) => {
    const result = await api.serviceOrders.deleteServiceOrderStatus({
      serviceOrderStatusId,
    });

    const serviceOrderStatus = result?.data.deleteServiceOrderStatus;

    return serviceOrderStatus;
  }
);

export const createServiceOrder = createAsyncThunk(
  'serviceOrders/createServiceOrder',
  async (payload) => {
    const result = await api.serviceOrders.createServiceOrder({
      data: {
        leadTechnicianId: payload?.leadTechnicianId || null,
        deviceId: payload.deviceId,
        title: payload.name,
        statusId: payload.columnId,
        description: payload?.description || '',
        isFinished: payload.completed,
        priority: payload.priority,
        technicians: payload.technicians,
        scheduledAt: payload.scheduledAt,
      },
    });

    const newServiceOrder = result?.data.createServiceOrder;

    return {
      ...newServiceOrder,
      name: newServiceOrder?.title,
      completed: newServiceOrder.isFinished,
      comments: newServiceOrder.serviceOrderLogs,
    };
  }
);

export const updateServiceOrderById = createAsyncThunk(
  'serviceOrders/updateServiceOrderById',
  async (payload) => {
    const { id, columnId, ...data } = payload;

    const result = await api.serviceOrders.updateServiceOrderById({
      serviceOrderId: id,
      data,
    });

    const newServiceOrder = result?.data.updateServiceOrder;

    return {
      ...newServiceOrder,
      name: newServiceOrder?.title,
      completed: newServiceOrder.isFinished,
      comments: newServiceOrder.serviceOrderLogs,
      columnId,
    };
  }
);

export const deleteServiceOrderById = createAsyncThunk(
  'serviceOrders/deleteServiceOrderById',
  async (payload) => {
    const { serviceOrderId, serviceOrderStatusId } = payload;

    const result = await api.serviceOrders.deleteServiceOrderById({
      serviceOrderId,
    });

    return {
      serviceOrderId: result.data?.deleteServiceOrder.id ?? '',
      serviceOrderStatusId,
    };
  }
);

export const addServiceOrderLog = createAsyncThunk(
  'serviceOrders/addServiceOrderLog',
  async (payload) => {
    const newComment = await api.serviceOrders.addServiceOrderLog({
      data: payload,
    });

    return newComment.data?.addServiceOrderLog;
  }
);

export const addServiceOrderSubtask = createAsyncThunk(
  'serviceOrders/addServiceOrderSubtask',
  async (payload) => {
    const newServiceOrderSubtask =
      await api.serviceOrders.addServiceOrderSubTask(payload);

    return newServiceOrderSubtask.data.createServiceOrderTask;
  }
);

export const updateServiceOrderSubtask = createAsyncThunk(
  'serviceOrders/updateServiceOrderSubtask',
  async (payload) => {
    const serviceOrderSubtask =
      await api.serviceOrders.updateServiceOrderSubTask(payload);

    return serviceOrderSubtask.data.updateServiceOrderTask;
  }
);

export const deleteServiceOrderSubtask = createAsyncThunk(
  'serviceOrders/deleteServiceOrderSubtask',
  async (id) => {
    const result = await api.serviceOrders.deleteServiceOrderSubTask({
      id,
    });

    return result.data.deleteServiceOrderTask;
  }
);
